import * as Sentry from '@sentry/nextjs'
import Router from 'next/router'

import { isWebsiteLocale } from '@/types/locales'

import { CHECKOUT_ROUTES, SIGN_UP_ROUTES } from '@/lib/sentry/performanceTracing'

/**
 * Times step 5b of the paid journey, sign up (see `docs/data-flows/paid-journey-steps.md`): from
 * the first sign-up interstitial (`/signup`, `/email`, `/login`) a signed-out shopper is shown
 * until they leave for checkout. The E042 funnel-simplification variant redirects straight on, so
 * its step 5b is the cost of that redirect.
 *
 * One transaction per visit to the interstitials, named after the first route (so `tracesSampler`
 * and dashboards treat it like the page) with op `checkout.sign_up`, from that page's navigation
 * start. It carries a `sign_up_outcome` attribute: `checkout`, `left` (to any other page) or
 * `abandoned` (page hidden). Only `checkout` sets the step 5b measurement. Each Firebase sign-in
 * inside it is a child span `checkout.sign_up.sign_in` with `sign_in_method` and `sign_in_result`.
 */
const OP = 'checkout.sign_up'
const SIGN_IN_OP = 'checkout.sign_up.sign_in'

export type SignUpOutcome = 'checkout' | 'left' | 'abandoned'
export type SignInMethod = 'anonymous' | 'google' | 'apple' | 'password'

/** Timing must never break signing up: on any Sentry failure the step is dropped. */
const guarded =
  <A extends unknown[]>(fn: (...args: A) => void) =>
  (...args: A): void => {
    try {
      fn(...args)
    } catch {
      // Dropped: a lost timing is better than a sign-up flow that throws.
    }
  }

/** Seconds since the epoch for a `performance.now()` reading, as Sentry span times expect. */
const toTimestamp = (now: number): number => (performance.timeOrigin + now) / 1000

/** The pages-router route for a URL: `/en-gb/daily30/signup?x=1` → `/[locale]/daily30/signup`. */
export const toRoute = (url: string): string => {
  const segments = new URL(url, 'https://zoe.com').pathname.split('/').filter(Boolean)
  if (segments[0] && isWebsiteLocale(segments[0])) segments[0] = '[locale]'
  return `/${segments.join('/')}`
}

let signUp: { transaction: NonNullable<ReturnType<typeof Sentry.startInactiveSpan>>; startedAt: number } | undefined
// `performance.now()` at the start of the latest client-side navigation.
let navigationStartedAt = 0

const start = (route: string, startedAt: number) => {
  const transaction = Sentry.startInactiveSpan({
    name: route,
    op: OP,
    forceTransaction: true,
    startTime: toTimestamp(startedAt),
  })
  if (transaction) signUp = { transaction, startedAt }
}

const end = guarded((outcome: SignUpOutcome) => {
  if (!signUp) return
  const { transaction, startedAt } = signUp
  signUp = undefined
  const endedAt = performance.now()
  if (outcome === 'checkout') {
    Sentry.withActiveSpan(transaction, () =>
      Sentry.setMeasurement('journey.step5b_sign_up', Math.round(endedAt - startedAt), 'millisecond'),
    )
  }
  transaction.setAttribute('sign_up_outcome', outcome)
  transaction.setStatus(outcome === 'checkout' ? 'ok' : 'cancelled')
  transaction.end(toTimestamp(endedAt))
})

/** Call once, after `Sentry.init`. Follows the router into and out of the sign-up interstitials. */
export const trackSignUpTiming = guarded((): void => {
  if (typeof window === 'undefined' || typeof performance === 'undefined') return

  const firstRoute = Router.router?.pathname ?? window.__NEXT_DATA__?.page
  if (firstRoute && SIGN_UP_ROUTES.has(firstRoute)) start(firstRoute, 0)

  Router.events.on(
    'routeChangeStart',
    guarded((url: string) => {
      navigationStartedAt = performance.now()
      if (!signUp) return
      const route = toRoute(url)
      // Moving between interstitials (sign up → email) is still the same step.
      if (SIGN_UP_ROUTES.has(route)) return
      end(CHECKOUT_ROUTES.has(route) ? 'checkout' : 'left')
    }),
  )
  Router.events.on(
    'routeChangeComplete',
    guarded((url: string) => {
      const route = toRoute(url)
      if (!signUp && SIGN_UP_ROUTES.has(route)) start(route, navigationStartedAt)
    }),
  )
  window.addEventListener('pagehide', () => end('abandoned'))
})

/**
 * Wraps a Firebase sign-in so its time shows as a child span of the sign-up step. Outside the
 * sign-up interstitials, or on any Sentry failure, it just runs `signIn`.
 */
export const timeSignIn = async <T extends { error: string | null }>(
  method: SignInMethod,
  signIn: () => Promise<T>,
): Promise<T> => {
  let span: ReturnType<typeof Sentry.startInactiveSpan> | undefined
  try {
    if (signUp) {
      const { transaction } = signUp
      transaction.setAttribute('sign_in_method', method)
      span = Sentry.withActiveSpan(transaction, () =>
        Sentry.startInactiveSpan({ name: `Sign in (${method})`, op: SIGN_IN_OP }),
      )
    }
  } catch {
    span = undefined
  }
  const result = await signIn()
  try {
    span?.setAttribute('sign_in_method', method)
    span?.setAttribute('sign_in_result', result.error ? 'error' : 'ok')
    span?.setStatus(result.error ? 'unknown_error' : 'ok')
    span?.end()
  } catch {
    // Dropped, as above.
  }
  return result
}
